import Link from 'next/link';
import { cn } from '@discover-lib/utils';
import { cuisineHref, type CuisineOption } from '@/lib/cityCuisine';
import { TrackProvider } from '@/components/analytics/context/AnalyticsContext';
import { TrackLink } from '@/components/analytics/hocs/withTrackableNavLinkComponent';

type CityCuisineLinksProps = {
  locationPath: string;
  locationTitle: string;
  cuisines: CuisineOption[];
  activeSlug?: string;
  className?: string;
};

// Stays mounted at every breakpoint so the cuisine routes are in the rendered page even where the filter sidebar is hidden.
export default function CityCuisineLinks({
  locationPath,
  locationTitle,
  cuisines,
  activeSlug,
  className,
}: CityCuisineLinksProps) {
  if (!cuisines.length) return null;

  return (
    <TrackProvider component_name="cuisine directory">
      <nav aria-labelledby="cuisine-directory-heading" className={cn('mb-8 sm:mb-12', className)}>
        <h2 id="cuisine-directory-heading" className="mb-5 mt-0 text-xl md:text-3xl">
          {locationTitle} restaurants by cuisine
        </h2>
        <ul className="m-0 flex list-none flex-wrap gap-2 p-0">
          {cuisines.map(({ id, title, slug }) => (
            <li key={id}>
              <TrackProvider link_name={`${title} cuisine link`}>
                <TrackLink>
                  <Link
                    href={cuisineHref(locationPath, slug)}
                    prefetch={false}
                    aria-current={slug === activeSlug ? 'page' : undefined}
                    className="inline-block rounded-full border border-ds-gray-light-200 px-3 py-1 text-sm text-neutral-800 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 aria-[current=page]:border-neutral-800 aria-[current=page]:font-bold">
                    {title}
                  </Link>
                </TrackLink>
              </TrackProvider>
            </li>
          ))}
        </ul>
      </nav>
    </TrackProvider>
  );
}
